/* PastéBCN — Product gallery
   Web Component <pb-product-gallery>:
   - Sincroniza dots (mobile) y thumbs (desktop) con el scroll del main.
   - Click en thumb/dot navega a la imagen correspondiente.
   - Soporta swipe nativo (overflow-x: scroll) en mobile.
   - Escucha cambios de variante (PUB_SUB_EVENTS.optionValueSelectionChange
     o evento custom 'pb:variant-change' con featured_image) para hacer
     swap a la imagen featured de la variante seleccionada.
   - Sobrevive a shopify:section:load reinicializándose. */

if (!customElements.get('pb-product-gallery')) {
  class PbProductGallery extends HTMLElement {
    connectedCallback() {
      this.main = this.querySelector('[data-gallery-main]');
      this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
      this.dots = Array.from(this.querySelectorAll('[data-gallery-dot]'));

      if (!this.main || this.slides.length === 0) return;

      this.activeMediaId = this.slides[0].dataset.mediaId;

      this._onThumbClick = this._onThumbClick.bind(this);
      this._onDotClick = this._onDotClick.bind(this);
      this._onScroll = this._onScroll.bind(this);
      this._onVariantChange = this._onVariantChange.bind(this);

      this.thumbs.forEach((btn) => btn.addEventListener('click', this._onThumbClick));
      this.dots.forEach((btn) => btn.addEventListener('click', this._onDotClick));

      this._scrollTicking = false;
      this.main.addEventListener('scroll', this._onScroll, { passive: true });

      document.addEventListener('pb:variant-change', this._onVariantChange);
      if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.optionValueSelectionChange) {
        this._unsub = window.subscribe(window.PUB_SUB_EVENTS.optionValueSelectionChange, (payload) => {
          this._handleDawnVariantChange(payload);
        });
      }
    }

    disconnectedCallback() {
      this.thumbs.forEach((btn) => btn.removeEventListener('click', this._onThumbClick));
      this.dots.forEach((btn) => btn.removeEventListener('click', this._onDotClick));
      if (this.main) this.main.removeEventListener('scroll', this._onScroll);
      document.removeEventListener('pb:variant-change', this._onVariantChange);
      if (this._unsub) this._unsub();
    }

    _onThumbClick(e) {
      e.preventDefault();
      const id = e.currentTarget.dataset.mediaId;
      this.goTo(id, { scrollMain: true, smooth: !this._reducedMotion() });
    }

    _onDotClick(e) {
      e.preventDefault();
      const id = e.currentTarget.dataset.mediaId;
      this.goTo(id, { scrollMain: true, smooth: !this._reducedMotion() });
    }

    _reducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    /* Detecta la slide activa por la posición de scroll horizontal. */
    _onScroll() {
      if (this._scrollTicking) return;
      this._scrollTicking = true;
      window.requestAnimationFrame(() => {
        const center = this.main.scrollLeft + this.main.clientWidth / 2;
        let closestSlide = null;
        let closestDelta = Infinity;
        this.slides.forEach((slide) => {
          const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
          const delta = Math.abs(center - slideCenter);
          if (delta < closestDelta) {
            closestDelta = delta;
            closestSlide = slide;
          }
        });
        if (closestSlide && closestSlide.dataset.mediaId !== this.activeMediaId) {
          this._setActive(closestSlide.dataset.mediaId);
        }
        this._scrollTicking = false;
      });
    }

    _onVariantChange(event) {
      const mediaId = event?.detail?.featured_media_id;
      if (mediaId) this.goTo(String(mediaId), { scrollMain: true, smooth: !this._reducedMotion() });
    }

    _handleDawnVariantChange() {
      /* Dawn emite optionValueSelectionChange. Si tu page lleva el
         payload con featured_media_id, lo usarías. Por defecto no
         actuamos para no pisar la slide actual. */
    }

    goTo(mediaId, { scrollMain = false, smooth = true } = {}) {
      const target = this.slides.find((s) => s.dataset.mediaId === mediaId);
      if (!target) return;
      this._setActive(mediaId);
      if (scrollMain && this.main) {
        this.main.scrollTo({
          left: target.offsetLeft - this.main.offsetLeft,
          behavior: smooth ? 'smooth' : 'auto',
        });
      }
    }

    _setActive(mediaId) {
      this.activeMediaId = mediaId;
      this.slides.forEach((slide) => {
        const on = slide.dataset.mediaId === mediaId;
        slide.classList.toggle('is-active', on);
        slide.setAttribute('aria-hidden', on ? 'false' : 'true');
      });
      this.thumbs.forEach((btn) => {
        const on = btn.dataset.mediaId === mediaId;
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      this.dots.forEach((btn) => {
        const on = btn.dataset.mediaId === mediaId;
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    }
  }

  customElements.define('pb-product-gallery', PbProductGallery);
}
